import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router';
import { forgotPasswordSchema, type ForgotPasswordInput } from '@fas/validation';
import { Alert, Button, Field, Input } from '@fas/ui';
import { apiRequest, errorMessage } from '@/lib/api';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const form = useForm<ForgotPasswordInput>({ resolver: zodResolver(forgotPasswordSchema), defaultValues: { email: '' } });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const { data } = await apiRequest<{ message: string }>('POST', '/auth/forgot-password', { body: values, auth: false });
      setSent(data.message);
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <AuthLayout title="Reset your password" subtitle="We will e-mail you a secure, time-limited link">
      {sent ? (
        <div className="space-y-4">
          <Alert tone="success">{sent}</Alert>
          <Link to="/login" className="block text-center text-sm font-medium text-blue-700 hover:underline">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form noValidate onSubmit={onSubmit} className="space-y-4">
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <Field id="forgot-email" label="Email" error={form.formState.errors.email?.message} required>
            {(a11y) => <Input {...a11y} type="email" autoComplete="email" autoFocus {...form.register('email')} />}
          </Field>
          <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
            Send reset link
          </Button>
          <Link to="/login" className="block text-center text-sm font-medium text-blue-700 hover:underline">
            Back to sign in
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}
